import tinycolor from 'tinycolor2';
import { getFontFamily, getFontLinks } from '@src/lib/fonts';

function buildVariants(color) {
  return {
    a1: color.setAlpha(0.1).toRgbString(),
    a2: color.setAlpha(0.2).toRgbString(),
    a3: color.setAlpha(0.3).toRgbString(),
    a4: color.setAlpha(0.4).toRgbString(),
    a5: color.setAlpha(0.5).toRgbString(),
    a6: color.setAlpha(0.6).toRgbString(),
    a7: color.setAlpha(0.7).toRgbString(),
    a8: color.setAlpha(0.8).toRgbString(),
    a9: color.setAlpha(0.9).toRgbString(),
    a10: color.setAlpha(1).toRgbString(),
    isLight: color.isLight(),
    text: color.isLight() ? 'rgb(0, 0, 0)' : 'rgb(255, 255, 255)',
  };
}

const important = value => `${value} !important`;
const reduceProperties = (props, variant, start = undefined) => (Array.isArray(props) ? props : [ `${props}` ])
  .reduce((styles, prop) => ({ ...styles, [prop]: variant }), start);

function buildClasses(prefix, { a1, a2, a3, a4, a5, a6, a7, a8, a9, a10, text }) {
  const strengths = (prefix, properties, opts = {}) => ({
    [`${prefix}-a1`]: reduceProperties(properties, opts.important === true ? important(a1) : a1, opts.more),
    [`${prefix}-a2`]: reduceProperties(properties, opts.important === true ? important(a2) : a2, opts.more),
    [`${prefix}-a3`]: reduceProperties(properties, opts.important === true ? important(a3) : a3, opts.more),
    [`${prefix}-a4`]: reduceProperties(properties, opts.important === true ? important(a4) : a4, opts.more),
    [`${prefix}-a5`]: reduceProperties(properties, opts.important === true ? important(a5) : a5, opts.more),
    [`${prefix}-a6`]: reduceProperties(properties, opts.important === true ? important(a6) : a6, opts.more),
    [`${prefix}-a7`]: reduceProperties(properties, opts.important === true ? important(a7) : a7, opts.more),
    [`${prefix}-a8`]: reduceProperties(properties, opts.important === true ? important(a8) : a8, opts.more),
    [`${prefix}-a9`]: reduceProperties(properties, opts.important === true ? important(a9) : a9, opts.more),
  });

  return {
    // @TODO Deprecate the $PREFIX-color version in favour of our standised options
    // - $PREFIX-color(-semi-opaque)
    // - $PREFIX-background-color(-semi-opaque)
    // - $PREFIX-border-color(-semi-opaque)

    [`.${prefix}-color, .text-${prefix}`]: { 'color': important(a10) },
    [`.${prefix}-color-semi-opaque, .text-${prefix}-semi-opaque`]: { 'color': important(a5) },
    ...strengths(`.text-${prefix}`, 'color', { important: true }),

    [`.${prefix}-background-color, .bg-${prefix}`]: { 'background-color': important(a10) },
    [`.${prefix}-background-color-semi-opaque, .bg-${prefix}-semi-opaque`]: { 'background-color': important(a5) },
    ...strengths(`.bg-${prefix}`, 'background-color', { important: true }),

    [`.${prefix}-border-color, .border-${prefix}`]: { 'border-color': important(a10) },
    [`.${prefix}-border-color-semi-opaque, .border-${prefix}-semi-opaque`]: { 'border-color': important(a5) },
    ...strengths(`.border-${prefix}`, 'border-color', { important: true }),

    [`.btn-${prefix}`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },
    [`.btn-${prefix}:hover, .btn-${prefix}:disabled, .btn-${prefix}:focus`]: {
      'background-color': important(a8),
      'border-color': important(a8),
      'color': important(text),
    },
    [`.btn-outline-${prefix}`]: {
      'border-color': important(a10),
      'color': important(a10),
    },
    [`.btn-outline-${prefix}:hover, .btn-outline-${prefix}:disabled, .btn-outline-${prefix}.active, .show > .btn-outline-${prefix}.dropdown-toggle`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },
    [`.bg-${prefix}`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },
    [`.bg-${prefix}:hover, .bg-${prefix}:disabled, .bg-${prefix}:focus`]: {
      'background-color': important(a8),
      'border-color': important(a8),
      'color': important(text),
    },
    [`.bg-outline-${prefix}`]: {
      'border-color': important(a10),
      'color': important(a10),
    },
    [`.bg-outline-${prefix}:hover, .bg-outline-${prefix}:disabled, .bg-outline-${prefix}:focus`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },

    ...strengths(`.btn-${prefix}`, [ 'background-color', 'border-color' ], {
      important: true,
      more: { color: important(text) },
    }),

    [`.alert-${prefix}`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },
    [`.alert-outline-${prefix}`]: {
      'border-color': important(a10),
      'color': important(a10),
    },
    [`.badge-${prefix}`]: {
      'background-color': important(a10),
      'border-color': important(a10),
      'color': important(text),
    },
    [`.badge-outline-${prefix}`]: {
      'border-color': important(a10),
      'color': important(a10),
    },
  };
}

export function createPrimaryColourStyles(primaryColour) {
  const variants = buildVariants(tinycolor(primaryColour));

  return {
    ...buildClasses('primary', { ...variants, text: '#fff' }),

    '.pagination .active .page-link': {
      'background-color': important(variants.a10),
      'border-color': important(variants.a10),
      'color': '#fff',
    },
    '.pagination .page-link': {
      'color': variants.a10,
    },
    '.slider::-moz-range-thumb, .slider::-webkit-slider-thumb': {
      'background-color': important(variants.a10),
    },
    '.md-stepper-horizontal .md-step.active .md-step-circle': {
      'background-color': important(variants.a10),
    },
    '.dropdown-item:hover': {
      'background-color': important(variants.a1),
      'color': important(variants.a10),
    },
    '.multiselect-rlnx .multiselect__option--highlight, .multiselect-rlnx .multiselect__option--highlight:after, .multiselect-rlnx .multiselect__tag': {
      'background-color': variants.a10,
    },
    '.multiselect-rlnx .multiselect__tag-icon:focus, .multiselect-rlnx .multiselect__tag-icon:hover': {
      'background-color': important(variants.a1),
    },
    '.nav-link:hover, .navbar-brand:hover': {
      'color': variants.a7,
    },
    '.nav-pills .nav-link.active, .nav-pills .show>.nav-link': {
      'background-color': variants.a10,
      'color': '#fff',
    },
    '.custom-control-input:checked ~ .custom-control-label::before': {
      'background-color': variants.a10,
      'border-color': variants.a10,
      'color': '#fff',
    },
    '.daterangepicker td.active, .daterangepicker td.active:hover': {
      'background-color': variants.a10,
      'color': '#fff',
    },

    'a': {
      'color': variants.a10,
    },
    'a:hover': {
      'color': variants.a7,
    },
    '.btn-link': {
      color: variants.a10,
    },
  };
}
export function createSecondaryColourStyles(secondaryColour) {
  const variants = buildVariants(tinycolor(secondaryColour));

  return {
    ...buildClasses('secondary', variants),

    '.nav-link, .navbar-brand': {
      'color': variants.a5,
    },
    '.ag-theme-balham .ag-header, .ag-center-cols-container': {
      'color': variants.a10,
    },
    // '.black, .sort .dropdown .btn-secondary.dropdown-toggle, .sort .title': {
    //   'color': variants.a10,
    // },
    'body': {
      'color': variants.a8,
    },
    'h1, h2, h3, h4, h5, h6': {
      'color': variants.a10,
    },
  };
}
export function createBackgroundColourStyles(backgroundColour) {
  const backgroundVariants = buildVariants(tinycolor(backgroundColour || 'rgb(255, 255, 255)'));

  return {
    'body': {
      'background-color': backgroundVariants.a10,
    },
  };
}
export function createHeaderFontStyles(headerFont) {
  const family = getFontFamily(headerFont);

  return {
    'h1, h2, h3, h4, h5, h6': {
      'font-family': family,
      'font-weight': 400,
    },
  };
}
export function createBodyFontStyles(bodyFont) {
  const family = getFontFamily(bodyFont);

  return {
    'body': {
      'font-family': family,
      'letter-spacing': '0.02em',
    },
  };
}

export function buildCSS(styles) {
  const strings = [];

  for (const selector in styles) {
    if (styles.hasOwnProperty(selector)) {
      const properties = Object.keys(styles[selector]).reduce((r, key) => r.concat(`${key}: ${styles[selector][key]};`), []).join(' ');
      strings.push(`${selector} { ${properties} }`);
    }
  }

  return strings.join('\n');
}

export function minifyCode(content) {
  return `${content}`.split('\n').map(s => s.trim()).join(' ').trim();
}

export function createVueMetaBranding(head, branding, { isAdminUI, nonce } = {}) {
  const styles = [];

  if (branding.primaryColour) {
    const classes = createPrimaryColourStyles(branding.primaryColour);
    styles.push(buildCSS(classes));
  }
  if (branding.secondaryColour) {
    const classes = createSecondaryColourStyles(branding.secondaryColour);
    styles.push(buildCSS(classes));
  }
  if (branding.backgroundColour) {
    const classes = createBackgroundColourStyles(branding.backgroundColour);
    styles.push(buildCSS(classes));
  }
  if (branding.headerFont) {
    const classes = createHeaderFontStyles(branding.headerFont);
    styles.push(buildCSS(classes));
  }
  if (branding.bodyFont) {
    const classes = createBodyFontStyles(branding.bodyFont);
    styles.push(buildCSS(classes));
  }

  if (styles.length) {
    head.style = (Array.isArray(head.style) ? head.style : []).concat({
      vmid: 'compiledCSS',
      cssText: minifyCode(styles.join('\n')),
      type: 'text/css',
      nonce,
    });
  }

  if (branding.backgroundColour) {
    head.meta = (Array.isArray(head.meta) ? head.meta : []).concat({
      name: 'theme-color',
      content: branding.backgroundColour,
      vmid: 'theme-color',
    });
  }

  if (branding.headerFont || branding.bodyFont) {
    const { headerFont, bodyFont } = branding;
    const links = [].concat(headerFont ? getFontLinks(headerFont) : [], bodyFont ? getFontLinks(bodyFont) : []);
    head.link = (Array.isArray(head.link) ? head.link : []).concat(links.map(link => ({ ...link, nonce })));
  }

  if (branding.customCSS) {
    head.style = (Array.isArray(head.style) ? head.style : []).concat({
      vmid: 'customCSS',
      cssText: minifyCode(branding.customCSS),
      type: 'text/css',
      nonce,
    });
  }

  if (!isAdminUI && branding.customJS) {
    head.script = (Array.isArray(head.script) ? head.script : []).concat({
      vmid: 'customJS',
      type: 'text/javascript',
      innerHTML: minifyCode(branding.customJS),
      body: true,
      nonce,
    });
    head.__dangerouslyDisableSanitizersByTagID = {
      ...head.__dangerouslyDisableSanitizersByTagID,
      customJS: [ 'innerHTML' ],
    };
  }

  return head;
}

export function createSingleSignOnButton(type, handle) {
  const result = {};

  if (type === 'SAML') {
    if (handle.includes('-azure-')) {
      result.name = 'Azure';
      result.icon = [ 'fab', 'windows' ];
      result.classes = [ 'type-azure' ];
    } else if (handle.includes('-microsoft-')) {
      result.name = 'Microsoft';
      result.icon = [ 'fab', 'windows' ];
      result.classes = [ 'type-microsoft' ];
    } else if (handle.includes('-google-')) {
      result.name = 'Google';
      result.icon = [ 'fab', 'google' ];
      result.classes = [ 'type-google' ];
    } else if (handle.includes('-okta-')) {
      result.name = 'Okta';
      result.icon = 'user';
      result.classes = [ 'type-okta' ];
    } else if (handle.includes('-onelogin-')) {
      result.name = 'OneLogin';
      result.icon = 'user';
      result.classes = [ 'type-onelogin' ];
    } else {
      result.name = 'SAML';
      result.icon = 'user';
      result.classes = [ 'type-saml' ];
    }
  } else if (type === 'OIDC') {
    result.name = 'OpenID';
    result.icon = 'user';
    result.classes = [ 'type-oidc' ];
  } else if (type === 'Facebook') {
    result.name = 'Facebook';
    result.icon = [ 'fab', 'facebook-f' ];
    result.classes = [ 'type-facebook' ];
  } else if (type === 'Google') {
    result.name = 'Google';
    result.icon = [ 'fab', 'google' ];
    result.classes = [ 'type-google' ];
  } else if (type === 'LoginWithAmazon') {
    result.name = 'Amazon';
    result.icon = [ 'fab', 'amazon' ];
    result.classes = [ 'type-amazon' ];
  }

  if (Array.isArray(result.classes)) {
    result.classes.push(`handle-${handle}`);
  }

  return result;
}



// WEBPACK FOOTER //
// ./src/lib/branding.js